// Tremor Date Picker [v1.0.4]
"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import { Time } from "@internationalized/date";
import * as PopoverPrimitives from "@radix-ui/react-popover";
import { useDateSegment, useTimeField, } from "@react-aria/datepicker";
import { useTimeFieldState, } from "@react-stately/datepicker";
import { RiCalendar2Fill, RiSubtractFill } from "@remixicon/react";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { tv } from "tailwind-variants";
import { cx, focusInput, focusRing, hasErrorInput } from "../../lib/utils";
import { Button } from "../Button";
import { Calendar as CalendarPrimitive } from "../Calendar";
//#region TimeInput
// ============================================================================
const isBrowserLocaleClockType24h = () => {
    const language = typeof window !== "undefined" ? window.navigator.language : "en-US";
    const hr = new Intl.DateTimeFormat(language, {
        hour: "numeric",
    }).format();
    return Number.isInteger(Number(hr));
};
const TimeSegment = ({ segment, state }) => {
    const ref = React.useRef(null);
    const { segmentProps } = useDateSegment(segment, state, ref);
    const isColon = segment.type === "literal" && segment.text === ":";
    const isSpace = segment.type === "literal" && segment.text === " ";
    const isDecorator = isColon || isSpace;
    return (React.createElement("div", Object.assign({}, segmentProps, { ref: ref, className: cx(
        // base
        "onvo-relative onvo-block onvo-w-full onvo-appearance-none onvo-rounded-md onvo-border onvo-px-2.5 onvo-py-1.5 onvo-text-left onvo-uppercase onvo-tabular-nums onvo-shadow-sm onvo-outline-none onvo-transition sm:onvo-text-sm", 
        // border color
        "onvo-border-gray-300 dark:onvo-border-gray-800", 
        // text color
        "onvo-text-gray-900 dark:onvo-text-gray-50", 
        // background color
        "onvo-bg-white dark:onvo-bg-gray-950", 
        // focus
        focusInput, 
        // invalid (optional)
        "invalid:onvo-border-red-500 invalid:onvo-ring-2 invalid:onvo-ring-red-200 group-aria-[invalid=true]/time-input:onvo-border-red-500 group-aria-[invalid=true]/time-input:onvo-ring-2 group-aria-[invalid=true]/time-input:onvo-ring-red-200 group-aria-[invalid=true]/time-input:dark:onvo-ring-red-400/20", {
            "!onvo-w-fit onvo-border-none onvo-bg-transparent onvo-px-0 onvo-text-gray-400 onvo-shadow-none": isDecorator,
            hidden: isSpace,
            "onvo-border-gray-300 onvo-bg-gray-100 onvo-text-gray-400 dark:onvo-border-gray-700 dark:onvo-bg-gray-800 dark:onvo-text-gray-500": state.isDisabled,
            "!onvo-bg-transparent !onvo-text-gray-400": !segment.isEditable,
        }) }),
        React.createElement("span", { "aria-hidden": "true", className: cx("onvo-pointer-events-none onvo-block w-full onvo-text-left onvo-text-gray-700 sm:onvo-text-sm", {
                hidden: !segment.isPlaceholder,
                "onvo-h-0": !segment.isPlaceholder,
            }) }, segment.placeholder),
        segment.isPlaceholder ? "" : segment.text));
};
const TimeInput = React.forwardRef((_a, ref) => {
    var { hourCycle } = _a, props = __rest(_a, ["hourCycle"]);
    const innerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => innerRef === null || innerRef === void 0 ? void 0 : innerRef.current);
    const locale = window !== undefined ? window.navigator.language : "en-US";
    const state = useTimeFieldState(Object.assign({ hourCycle: hourCycle, locale: locale, shouldForceLeadingZeros: true, autoFocus: true }, props));
    const { fieldProps } = useTimeField(Object.assign(Object.assign({}, props), { hourCycle: hourCycle, shouldForceLeadingZeros: true }), state, innerRef);
    return (React.createElement("div", Object.assign({}, fieldProps, { ref: innerRef, className: "onvo-group/time-input onvo-inline-flex onvo-w-full onvo-gap-x-2" }), state.segments.map((segment, i) => (React.createElement(TimeSegment, { key: i, segment: segment, state: state })))));
});
TimeInput.displayName = "TimeInput";
//#region Trigger
// ============================================================================
const triggerStyles = tv({
    base: [
        // base
        "onvo-peer onvo-flex onvo-w-full onvo-cursor-pointer onvo-appearance-none onvo-items-center onvo-gap-x-2 onvo-truncate onvo-rounded-md onvo-border onvo-px-3 onvo-py-1.5 onvo-shadow-sm onvo-outline-none onvo-transition-all sm:onvo-text-sm",
        // background color
        "onvo-bg-white dark:onvo-bg-gray-950",
        // border color
        "onvo-border-gray-200 dark:onvo-border-gray-800",
        // text color
        "onvo-text-gray-900 dark:onvo-text-gray-50",
        // placeholder color
        "onvo-placeholder-gray-400 dark:onvo-placeholder-gray-500",
        // hover
        "hover:onvo-bg-gray-50 hover:dark:onvo-bg-gray-950/50",
        // disabled
        "disabled:onvo-pointer-events-none",
        "disabled:onvo-bg-gray-100 disabled:onvo-text-gray-400",
        "disabled:dark:onvo-border-gray-800 disabled:dark:onvo-bg-gray-800 disabled:dark:onvo-text-gray-500",
        // focus
        focusInput,
        // invalid (optional)
        // "aria-[invalid=true]:dark:ring-red-400/20 aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-red-200 aria-[invalid=true]:border-red-500 invalid:ring-2 invalid:ring-red-200 invalid:border-red-500"
    ],
    variants: {
        hasError: {
            true: hasErrorInput,
        },
    },
});
const Trigger = React.forwardRef((_a, forwardedRef) => {
    var { className, children, placeholder, hasError } = _a, props = __rest(_a, ["className", "children", "placeholder", "hasError"]);
    return (React.createElement(PopoverPrimitives.Trigger, { asChild: true },
        React.createElement("button", Object.assign({ ref: forwardedRef, className: cx(triggerStyles({ hasError }), className) }, props),
            React.createElement(RiCalendar2Fill, { className: "onvo-size-4 onvo-shrink-0 onvo-text-gray-400 dark:onvo-text-gray-600" }),
            React.createElement("span", { className: "onvo-flex-1 onvo-overflow-hidden onvo-text-ellipsis onvo-whitespace-nowrap onvo-text-left onvo-text-gray-900 dark:onvo-text-gray-50" }, children ? (children) : placeholder ? (React.createElement("span", { className: "onvo-text-gray-400 dark:onvo-text-gray-600" }, placeholder)) : null))));
});
Trigger.displayName = "DatePicker.Trigger";
//#region Popover
// ============================================================================
const CalendarPopover = React.forwardRef((_a, forwardedRef) => {
    var { align, className, children } = _a, props = __rest(_a, ["align", "className", "children"]);
    return (React.createElement(PopoverPrimitives.Portal, null,
        React.createElement(PopoverPrimitives.Content, Object.assign({ ref: forwardedRef, sideOffset: 10, side: "bottom", align: align, avoidCollisions: true, onOpenAutoFocus: (e) => e.preventDefault(), className: cx(
            // base
            "onvo-relative onvo-z-50 onvo-w-fit onvo-rounded-md onvo-border onvo-text-sm onvo-shadow-xl onvo-shadow-black/[2.5%]", 
            // widths
            "onvo-min-w-[calc(var(--radix-select-trigger-width)-2px)] onvo-max-w-[95vw]", 
            // border color
            "onvo-border-gray-200 dark:onvo-border-gray-800", 
            // background color
            "onvo-bg-white dark:onvo-bg-gray-950", 
            // transition
            "onvo-will-change-[transform,opacity]", "data-[state=closed]:onvo-animate-hide", "data-[state=open]:data-[side=bottom]:onvo-animate-slideDownAndFade data-[state=open]:data-[side=left]:onvo-animate-slideLeftAndFade data-[state=open]:data-[side=right]:onvo-animate-slideRightAndFade data-[state=open]:data-[side=top]:onvo-animate-slideUpAndFade", className) }, props), children)));
});
CalendarPopover.displayName = "DatePicker.CalendarPopover";
const PresetContainer = ({ 
// Available preset configurations
presets, 
// Event handler when a preset is selected
onSelect, 
// Currently selected preset
currentValue, }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isDateRangePresets = (preset) => {
        return "dateRange" in preset;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isDatePresets = (preset) => {
        return "date" in preset;
    };
    const handleClick = (preset) => {
        if (isDateRangePresets(preset)) {
            onSelect(preset.dateRange);
        }
        else if (isDatePresets(preset)) {
            onSelect(preset.date);
        }
    };
    const compareDates = (date1, date2) => {
        return (date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear());
    };
    const compareRanges = (range1, range2) => {
        const from1 = range1.from;
        const from2 = range2.from;
        let equalFrom = false;
        if (from1 && from2) {
            const sameFrom = compareDates(from1, from2);
            if (sameFrom) {
                equalFrom = true;
            }
        }
        const to1 = range1.to;
        const to2 = range2.to;
        let equalTo = false;
        if (to1 && to2) {
            const sameTo = compareDates(to1, to2);
            if (sameTo) {
                equalTo = true;
            }
        }
        return equalFrom && equalTo;
    };
    const matchesCurrent = (preset) => {
        if (isDateRangePresets(preset)) {
            const value = currentValue;
            return value && compareRanges(value, preset.dateRange);
        }
        else if (isDatePresets(preset)) {
            const value = currentValue;
            return value && compareDates(value, preset.date);
        }
        return false;
    };
    return (React.createElement("ul", { className: "onvo-flex onvo-items-start onvo-gap-x-2 sm:onvo-flex-col" }, presets.map((preset, index) => {
        return (React.createElement("li", { key: index, className: "sm:onvo-w-full sm:onvo-py-px" },
            React.createElement("button", { title: preset.label, className: cx(
                // base
                "onvo-relative onvo-w-full onvo-overflow-hidden onvo-text-ellipsis onvo-whitespace-nowrap onvo-rounded onvo-border onvo-px-2.5 onvo-py-1.5 onvo-text-left onvo-text-base onvo-shadow-sm onvo-outline-none onvo-transition-all sm:onvo-border-none sm:onvo-py-2 sm:onvo-text-sm sm:onvo-shadow-none", 
                // text color
                "onvo-text-gray-700 dark:onvo-text-gray-300", 
                // border color
                "onvo-border-gray-200 dark:onvo-border-gray-800", 
                // focus
                focusRing, 
                // background color
                "focus-visible:onvo-bg-gray-100 focus-visible:dark:onvo-bg-gray-900", "hover:onvo-bg-gray-100 hover:dark:onvo-bg-gray-900", {
                    "onvo-bg-gray-100 dark:onvo-bg-gray-900": matchesCurrent(preset),
                }), onClick: () => handleClick(preset), "aria-label": `Select ${preset.label}` },
                React.createElement("span", null, preset.label))));
    })));
};
PresetContainer.displayName = "DatePicker.PresetContainer";
//#region Date Picker Shared
// ============================================================================
const formatDate = (date, locale, includeTime) => {
    const usesAmPm = !isBrowserLocaleClockType24h();
    let dateString;
    if (includeTime) {
        dateString = usesAmPm
            ? format(date, "dd MMM, yyyy h:mm a", { locale })
            : format(date, "dd MMM, yyyy HH:mm", { locale });
    }
    else {
        dateString = format(date, "dd MMM, yyyy", { locale });
    }
    return dateString;
};
const SingleDatePicker = (_a) => {
    var _b, _c, _d;
    var { defaultValue, value, onChange, presets, disabled, disabledDays, disableNavigation, className, showTimePicker, placeholder = "Select date", hasError, translations, enableYearNavigation = false, locale = enUS, align = "center" } = _a, props = __rest(_a, ["defaultValue", "value", "onChange", "presets", "disabled", "disabledDays", "disableNavigation", "className", "showTimePicker", "placeholder", "hasError", "translations", "enableYearNavigation", "locale", "align"]);
    const [open, setOpen] = React.useState(false);
    const [date, setDate] = React.useState((_b = value !== null && value !== void 0 ? value : defaultValue) !== null && _b !== void 0 ? _b : undefined);
    const [month, setMonth] = React.useState(date);
    const [time, setTime] = React.useState(value
        ? new Time(value.getHours(), value.getMinutes())
        : defaultValue
            ? new Time(defaultValue.getHours(), defaultValue.getMinutes())
            : new Time(0, 0));
    const initialDate = React.useMemo(() => {
        return date;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    React.useEffect(() => {
        var _a;
        setDate((_a = value !== null && value !== void 0 ? value : defaultValue) !== null && _a !== void 0 ? _a : undefined);
    }, [value, defaultValue]);
    React.useEffect(() => {
        if (date) {
            setMonth(date);
        }
    }, [date]);
    React.useEffect(() => {
        if (!open) {
            setMonth(date);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const onCancel = () => {
        setDate(initialDate);
        setTime(initialDate
            ? new Time(initialDate.getHours(), initialDate.getMinutes())
            : new Time(0, 0));
        setOpen(false);
    };
    const onOpenChange = (open) => {
        if (!open) {
            onCancel();
        }
        setOpen(open);
    };
    const onDateChange = (date) => {
        const newDate = date;
        if (showTimePicker) {
            if (newDate && !time) {
                setTime(new Time(0, 0));
            }
            if (newDate && time) {
                newDate.setHours(time.hour);
                newDate.setMinutes(time.minute);
            }
        }
        setDate(newDate);
    };
    const onTimeChange = (time) => {
        if (!time)
            return;
        setTime(time);
        if (!date) {
            return;
        }
        const newDate = new Date(date.getTime());
        if (!time) {
            newDate.setHours(0);
            newDate.setMinutes(0);
        }
        else {
            newDate.setHours(time.hour);
            newDate.setMinutes(time.minute);
        }
        setDate(newDate);
    };
    const formattedDate = React.useMemo(() => {
        if (!date) {
            return null;
        }
        return formatDate(date, locale, showTimePicker);
    }, [date, locale, showTimePicker]);
    const onApply = () => {
        setOpen(false);
        onChange === null || onChange === void 0 ? void 0 : onChange(date);
    };
    React.useEffect(() => {
        var _a;
        setDate((_a = value !== null && value !== void 0 ? value : defaultValue) !== null && _a !== void 0 ? _a : undefined);
        setTime(value
            ? new Time(value.getHours(), value.getMinutes())
            : defaultValue
                ? new Time(defaultValue.getHours(), defaultValue.getMinutes())
                : new Time(0, 0));
    }, [value, defaultValue]);
    return (React.createElement(PopoverPrimitives.Root, { "tremor-id": "tremor-raw", open: open, onOpenChange: onOpenChange },
        React.createElement(Trigger, { placeholder: placeholder, disabled: disabled, className: className, hasError: hasError, "aria-required": props.required || props["aria-required"], "aria-invalid": props["aria-invalid"], "aria-label": props["aria-label"], "aria-labelledby": props["aria-labelledby"] }, formattedDate),
        React.createElement(CalendarPopover, { align: align },
            React.createElement("div", { className: "onvo-flex onvo-flex-col sm:onvo-flex-row sm:onvo-items-start" },
                presets && presets.length > 0 && (React.createElement("div", { className: cx("onvo-relative onvo-flex onvo-h-14 onvo-w-full onvo-items-center sm:onvo-h-auto sm:onvo-w-40", "onvo-border-b onvo-border-gray-200 sm:onvo-border-b-0 sm:onvo-border-r dark:onvo-border-gray-800", "onvo-overflow-auto") },
                    React.createElement("div", { className: "onvo-px-2 onvo-pr-2 sm:onvo-inset-0 sm:onvo-left-0 sm:onvo-py-2" },
                        React.createElement(PresetContainer, { currentValue: date, presets: presets, onSelect: onDateChange })))),
                React.createElement("div", null,
                    React.createElement(CalendarPrimitive, Object.assign({ mode: "single", month: month, onMonthChange: setMonth, selected: date, onSelect: onDateChange, disabled: disabledDays, locale: locale, enableYearNavigation: enableYearNavigation, disableNavigation: disableNavigation, initialFocus: true }, props)),
                    showTimePicker && (React.createElement("div", { className: "onvo-border-t onvo-border-gray-200 onvo-p-3 dark:onvo-border-gray-800" },
                        React.createElement(TimeInput, { "aria-label": "Time", onChange: onTimeChange, isDisabled: !date, value: time, isRequired: props.required }))),
                    React.createElement("div", { className: "onvo-flex onvo-items-center onvo-gap-x-2 onvo-border-t onvo-border-gray-200 onvo-p-3 dark:onvo-border-gray-800" },
                        React.createElement(Button, { variant: "secondary", className: "onvo-h-8 onvo-w-full", type: "button", onClick: onCancel }, (_c = translations === null || translations === void 0 ? void 0 : translations.cancel) !== null && _c !== void 0 ? _c : "Cancel"),
                        React.createElement(Button, { variant: "primary", className: "onvo-h-8 onvo-w-full", type: "button", onClick: onApply }, (_d = translations === null || translations === void 0 ? void 0 : translations.apply) !== null && _d !== void 0 ? _d : "Apply")))))));
};
const RangeDatePicker = (_a) => {
    var _b, _c, _d, _e, _f, _g;
    var { defaultValue, value, onChange, presets, disabled, disableNavigation, disabledDays, enableYearNavigation = false, locale = enUS, showTimePicker, placeholder = "Select date range", hasError, translations, align = "center", className, maxDays } = _a, props = __rest(_a, ["defaultValue", "value", "onChange", "presets", "disabled", "disableNavigation", "disabledDays", "enableYearNavigation", "locale", "showTimePicker", "placeholder", "hasError", "translations", "align", "className", "maxDays"]);
    const [open, setOpen] = React.useState(false);
    const [range, setRange] = React.useState((_b = value !== null && value !== void 0 ? value : defaultValue) !== null && _b !== void 0 ? _b : undefined);
    const [month, setMonth] = React.useState(range === null || range === void 0 ? void 0 : range.from);
    const [startTime, setStartTime] = React.useState((value === null || value === void 0 ? void 0 : value.from)
        ? new Time(value.from.getHours(), value.from.getMinutes())
        : (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.from)
            ? new Time(defaultValue.from.getHours(), defaultValue.from.getMinutes())
            : new Time(0, 0));
    const [endTime, setEndTime] = React.useState((value === null || value === void 0 ? void 0 : value.to)
        ? new Time(value.to.getHours(), value.to.getMinutes())
        : (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.to)
            ? new Time(defaultValue.to.getHours(), defaultValue.to.getMinutes())
            : new Time(0, 0));
    const initialRange = React.useMemo(() => {
        return range;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    React.useEffect(() => {
        var _a;
        setRange((_a = value !== null && value !== void 0 ? value : defaultValue) !== null && _a !== void 0 ? _a : undefined);
    }, [value, defaultValue]);
    React.useEffect(() => {
        if (range) {
            setMonth(range.from);
        }
    }, [range]);
    React.useEffect(() => {
        if (!open) {
            setMonth(range === null || range === void 0 ? void 0 : range.from);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const onRangeChange = (range) => {
        const newRange = range;
        if (showTimePicker) {
            if ((newRange === null || newRange === void 0 ? void 0 : newRange.from) && !startTime) {
                setStartTime(new Time(0, 0));
            }
            if ((newRange === null || newRange === void 0 ? void 0 : newRange.to) && !endTime) {
                setEndTime(new Time(0, 0));
            }
            if ((newRange === null || newRange === void 0 ? void 0 : newRange.from) && startTime) {
                newRange.from.setHours(startTime.hour);
                newRange.from.setMinutes(startTime.minute);
            }
            if ((newRange === null || newRange === void 0 ? void 0 : newRange.to) && endTime) {
                newRange.to.setHours(endTime.hour);
                newRange.to.setMinutes(endTime.minute);
            }
        }
        setRange(newRange);
    };
    const onCancel = () => {
        setRange(initialRange);
        setStartTime((initialRange === null || initialRange === void 0 ? void 0 : initialRange.from)
            ? new Time(initialRange.from.getHours(), initialRange.from.getMinutes())
            : new Time(0, 0));
        setEndTime((initialRange === null || initialRange === void 0 ? void 0 : initialRange.to)
            ? new Time(initialRange.to.getHours(), initialRange.to.getMinutes())
            : new Time(0, 0));
        setOpen(false);
    };
    const onOpenChange = (open) => {
        if (!open) {
            onCancel();
        }
        setOpen(open);
    };
    const onTimeChange = (time, pos) => {
        if (!time)
            return;
        switch (pos) {
            case "start":
                setStartTime(time);
                break;
            case "end":
                setEndTime(time);
                break;
        }
        if (!range) {
            return;
        }
        if (pos === "start") {
            if (!range.from) {
                return;
            }
            const newDate = new Date(range.from.getTime());
            if (!time) {
                newDate.setHours(0);
                newDate.setMinutes(0);
            }
            else {
                newDate.setHours(time.hour);
                newDate.setMinutes(time.minute);
            }
            setRange(Object.assign(Object.assign({}, range), { from: newDate }));
        }
        if (pos === "end") {
            if (!range.to) {
                return;
            }
            const newDate = new Date(range.to.getTime());
            if (!time) {
                newDate.setHours(0);
                newDate.setMinutes(0);
            }
            else {
                newDate.setHours(time.hour);
                newDate.setMinutes(time.minute);
            }
            setRange(Object.assign(Object.assign({}, range), { to: newDate }));
        }
    };
    React.useEffect(() => {
        var _a;
        setRange((_a = value !== null && value !== void 0 ? value : defaultValue) !== null && _a !== void 0 ? _a : undefined);
        setStartTime((value === null || value === void 0 ? void 0 : value.from)
            ? new Time(value.from.getHours(), value.from.getMinutes())
            : (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.from)
                ? new Time(defaultValue.from.getHours(), defaultValue.from.getMinutes())
                : new Time(0, 0));
        setEndTime((value === null || value === void 0 ? void 0 : value.to)
            ? new Time(value.to.getHours(), value.to.getMinutes())
            : (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.to)
                ? new Time(defaultValue.to.getHours(), defaultValue.to.getMinutes())
                : new Time(0, 0));
    }, [value, defaultValue]);
    const displayRange = React.useMemo(() => {
        if (!range) {
            return null;
        }
        return `${range.from ? formatDate(range.from, locale, showTimePicker) : ""} - ${range.to ? formatDate(range.to, locale, showTimePicker) : ""}`;
    }, [range, locale, showTimePicker]);
    const onApply = () => {
        setOpen(false);
        onChange === null || onChange === void 0 ? void 0 : onChange(range);
    };
    return (React.createElement(PopoverPrimitives.Root, { "tremor-id": "tremor-raw", open: open, onOpenChange: onOpenChange },
        React.createElement(Trigger, { placeholder: placeholder, disabled: disabled, className: className, hasError: hasError, "aria-required": props.required || props["aria-required"], "aria-invalid": props["aria-invalid"], "aria-label": props["aria-label"], "aria-labelledby": props["aria-labelledby"] }, displayRange),
        React.createElement(CalendarPopover, { align: align },
            React.createElement("div", { className: "onvo-flex" },
                React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-overflow-x-auto sm:onvo-flex-row sm:onvo-items-stretch" },
                    presets && presets.length > 0 && (React.createElement("div", { className: cx("onvo-relative onvo-flex onvo-h-16 onvo-w-full onvo-items-center sm:onvo-h-auto sm:onvo-w-40", "onvo-border-b onvo-border-gray-200 sm:onvo-border-b-0 sm:onvo-border-r dark:onvo-border-gray-800", "onvo-overflow-x-auto sm:onvo-overflow-y-auto") },
                        React.createElement("div", { className: "onvo-absolute onvo-px-3 sm:onvo-inset-0 sm:onvo-left-0 sm:onvo-p-2" },
                            React.createElement(PresetContainer, { currentValue: range, presets: presets, onSelect: onRangeChange })))),
                    React.createElement("div", { className: "onvo-overflow-x-auto" },
                        React.createElement(CalendarPrimitive, Object.assign({ mode: "range", selected: range, onSelect: onRangeChange, month: month, onMonthChange: setMonth, numberOfMonths: 2, disabled: disabledDays, disableNavigation: disableNavigation, enableYearNavigation: enableYearNavigation, max: maxDays, locale: locale, initialFocus: true, classNames: {
                                months: "onvo-flex onvo-flex-row onvo-divide-x onvo-divide-gray-200 dark:onvo-divide-gray-800 onvo-overflow-x-auto",
                            } }, props)),
                        showTimePicker && (React.createElement("div", { className: "onvo-flex onvo-items-center onvo-justify-evenly onvo-gap-x-3 onvo-border-t onvo-border-gray-200 onvo-p-3 dark:onvo-border-gray-800" },
                            React.createElement("div", { className: "onvo-flex onvo-flex-1 onvo-items-center onvo-gap-x-2" },
                                React.createElement("span", { className: "dark:onvo-text-gray-30 onvo-text-gray-700" }, (_c = translations === null || translations === void 0 ? void 0 : translations.start) !== null && _c !== void 0 ? _c : "Start",
                                    ":"),
                                React.createElement(TimeInput, { value: startTime, onChange: (v) => onTimeChange(v, "start"), "aria-label": "Start date time", isDisabled: !(range === null || range === void 0 ? void 0 : range.from), isRequired: props.required })),
                            React.createElement(RiSubtractFill, { className: "onvo-size-4 onvo-shrink-0 onvo-text-gray-400" }),
                            React.createElement("div", { className: "onvo-flex onvo-flex-1 onvo-items-center onvo-gap-x-2" },
                                React.createElement("span", { className: "dark:onvo-text-gray-30 onvo-text-gray-700" }, (_d = translations === null || translations === void 0 ? void 0 : translations.end) !== null && _d !== void 0 ? _d : "End",
                                    ":"),
                                React.createElement(TimeInput, { value: endTime, onChange: (v) => onTimeChange(v, "end"), "aria-label": "End date time", isDisabled: !(range === null || range === void 0 ? void 0 : range.to), isRequired: props.required })))),
                        React.createElement("div", { className: "onvo-border-t onvo-border-gray-200 onvo-p-3 sm:onvo-flex sm:onvo-items-center sm:onvo-justify-between dark:onvo-border-gray-800" },
                            React.createElement("p", { className: "onvo-tabular-nums onvo-text-gray-900 dark:onvo-text-gray-50" },
                                React.createElement("span", { className: "onvo-text-gray-700 dark:onvo-text-gray-300" }, (_e = translations === null || translations === void 0 ? void 0 : translations.range) !== null && _e !== void 0 ? _e : "Range",
                                    ":"),
                                " ",
                                React.createElement("span", { className: "onvo-font-medium" }, displayRange),
                                " ",
                                (range === null || range === void 0 ? void 0 : range.to) && (range === null || range === void 0 ? void 0 : range.from) && (React.createElement("p", { onClick: () => setRange(undefined), className: "onvo-underline onvo-cursor-pointer" }, "Reset"))),
                            React.createElement("div", { className: "onvo-mt-2 onvo-flex onvo-items-center onvo-gap-x-2 sm:onvo-mt-0" },
                                React.createElement(Button, { variant: "secondary", className: "onvo-h-8 onvo-w-full sm:onvo-w-fit", type: "button", onClick: onCancel }, (_f = translations === null || translations === void 0 ? void 0 : translations.cancel) !== null && _f !== void 0 ? _f : "Cancel"),
                                React.createElement(Button, { variant: "primary", className: "onvo-h-8 onvo-w-full sm:onvo-w-fit", type: "button", onClick: onApply }, (_g = translations === null || translations === void 0 ? void 0 : translations.apply) !== null && _g !== void 0 ? _g : "Apply")))))))));
};
//#region Preset Validation
// ============================================================================
const validatePresets = (presets, rules) => {
    const { toYear, fromYear, fromMonth, toMonth, fromDay, toDay } = rules;
    if (presets && presets.length > 0) {
        const fromYearToUse = fromYear;
        const toYearToUse = toYear;
        presets.forEach((preset) => {
            var _a, _b, _c, _d, _e, _f;
            if ("date" in preset) {
                const presetYear = preset.date.getFullYear();
                if (fromYear && presetYear < fromYear) {
                    throw new Error(`Preset ${preset.label} is before fromYear ${fromYearToUse}.`);
                }
                if (toYear && presetYear > toYear) {
                    throw new Error(`Preset ${preset.label} is after toYear ${toYearToUse}.`);
                }
                if (fromMonth) {
                    const presetMonth = preset.date.getMonth();
                    if (presetMonth < fromMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label} is before fromMonth ${fromMonth}.`);
                    }
                }
                if (toMonth) {
                    const presetMonth = preset.date.getMonth();
                    if (presetMonth > toMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label} is after toMonth ${toMonth}.`);
                    }
                }
                if (fromDay) {
                    const presetDay = preset.date.getDate();
                    if (presetDay < fromDay.getDate()) {
                        throw new Error(`Preset ${preset.label} is before fromDay ${fromDay}.`);
                    }
                }
                if (toDay) {
                    const presetDay = preset.date.getDate();
                    if (presetDay > toDay.getDate()) {
                        throw new Error(`Preset ${preset.label} is after toDay ${format(toDay, "MMM dd, yyyy")}.`);
                    }
                }
            }
            if ("dateRange" in preset) {
                const presetFromYear = (_a = preset.dateRange.from) === null || _a === void 0 ? void 0 : _a.getFullYear();
                const presetToYear = (_b = preset.dateRange.to) === null || _b === void 0 ? void 0 : _b.getFullYear();
                if (presetFromYear && fromYear && presetFromYear < fromYear) {
                    throw new Error(`Preset ${preset.label}'s 'from' is before fromYear ${fromYearToUse}.`);
                }
                if (presetToYear && toYear && presetToYear > toYear) {
                    throw new Error(`Preset ${preset.label}'s 'to' is after toYear ${toYearToUse}.`);
                }
                if (fromMonth) {
                    const presetMonth = (_c = preset.dateRange.from) === null || _c === void 0 ? void 0 : _c.getMonth();
                    if (presetMonth && presetMonth < fromMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label}'s 'from' is before fromMonth ${format(fromMonth, "MMM, yyyy")}.`);
                    }
                }
                if (toMonth) {
                    const presetMonth = (_d = preset.dateRange.to) === null || _d === void 0 ? void 0 : _d.getMonth();
                    if (presetMonth && presetMonth > toMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label}'s 'to' is after toMonth ${format(toMonth, "MMM, yyyy")}.`);
                    }
                }
                if (fromDay) {
                    const presetDay = (_e = preset.dateRange.from) === null || _e === void 0 ? void 0 : _e.getDate();
                    if (presetDay && presetDay < fromDay.getDate()) {
                        throw new Error(`Preset ${preset.dateRange.from}'s 'from' is before fromDay ${format(fromDay, "MMM dd, yyyy")}.`);
                    }
                }
                if (toDay) {
                    const presetDay = (_f = preset.dateRange.to) === null || _f === void 0 ? void 0 : _f.getDate();
                    if (presetDay && presetDay > toDay.getDate()) {
                        throw new Error(`Preset ${preset.label}'s 'to' is after toDay ${format(toDay, "MMM dd, yyyy")}.`);
                    }
                }
            }
        });
    }
};
const DatePicker = (_a) => {
    var { presets } = _a, props = __rest(_a, ["presets"]);
    if (presets) {
        validatePresets(presets, props);
    }
    return React.createElement(SingleDatePicker, Object.assign({ presets: presets }, props));
};
DatePicker.displayName = "DatePicker";
const DateRangePicker = (_a) => {
    var { presets } = _a, props = __rest(_a, ["presets"]);
    if (presets) {
        validatePresets(presets, props);
    }
    return React.createElement(RangeDatePicker, Object.assign({ presets: presets }, props));
};
DateRangePicker.displayName = "DateRangePicker";
export { DatePicker, DateRangePicker, };
//# sourceMappingURL=DatePicker.js.map